import { useRef } from 'react';
import { GitProvider } from 'consts/git';
import { v4 as uuid4 } from 'uuid';
import { mixpanelService } from '../services/mixpanel';
import { MIXPANEL_OPERATIONS } from '../services/mixpanel/consts';
import { WizardStepAction } from '../shared/MultiStepDialog';
import { StepConfig } from '../shared/MultiStepDialog';

export type WizardAnalyticsConfig = {
  gitProvider: GitProvider;
  wizardVersion: string;
  steps: StepConfig[];
};

/**
 * Converts a kebab-case step ID to snake_case for use as a stable analytics step key.
 *
 * @param stepId - The kebab-case step identifier (e.g. `'organization-details'`)
 * @returns The snake_case equivalent (e.g. `'organization_details'`)
 *
 * @example
 * toStepKey('organization-details'); // Returns: 'organization_details'
 */
const toStepKey = (stepId: string) => stepId.replace(/-/g, '_');

/**
 * Extracts a plain string step name from a StepConfig title.
 * Falls back to the step ID if the title is not a plain string (e.g. JSX).
 *
 * @param step - The step configuration whose title should be resolved
 * @returns The title string when the title is a plain string, otherwise the step ID
 */
const toStepName = (step: StepConfig) =>
  typeof step.title === 'string' ? step.title : step.id;

/**
 * Shared analytics hook for git provider installation wizards.
 *
 * Generates a stable `wizard_session_id` for the lifetime of the wizard component,
 * tracks which steps have been visited (for `is_revisit`), and returns callbacks
 * compatible with `MultiStepDialog`'s `onStepViewed`, `onStepNavigated`, `onCancel`,
 * and the wizard's own `onComplete`.
 *
 * @param config - Analytics configuration for the wizard instance
 * @param config.gitProvider - The git provider identifier (e.g. `'github'`, `'azure_devops'`)
 * @param config.wizardVersion - Opaque version string used to distinguish wizard iterations in analytics
 * @param config.steps - The ordered step config array for the wizard; used to resolve step names
 * @returns An object containing `onStepViewed`, `onStepNavigated`, `onCancel`, and `onWizardCompleted` callbacks
 *
 * @example
 * const { onStepViewed, onStepNavigated, onCancel, onWizardCompleted } = useWizardAnalytics({
 *   gitProvider: 'github',
 *   wizardVersion: 'v1',
 *   steps: getSteps(),
 * });
 */
export const useWizardAnalytics = ({
  gitProvider,
  wizardVersion,
  steps,
}: WizardAnalyticsConfig) => {
  const wizardSessionId = useRef(uuid4());
  const visitedSteps = useRef(new Set<string>());
  const revisitedSteps = useRef(new Set<string>());

  const baseWizardDetails = {
    git_provider: gitProvider,
    wizard_session_id: wizardSessionId.current,
    wizard_version: wizardVersion,
    total_steps: steps.length,
  };

  const onStepViewed = (stepIndex: number, stepId: string) => {
    const isRevisit = visitedSteps.current.has(stepId);

    if (isRevisit) {
      revisitedSteps.current.add(stepId);
    }

    visitedSteps.current.add(stepId);

    const step = steps[stepIndex];

    mixpanelService.track(MIXPANEL_OPERATIONS.ONBOARDING_WIZARD_STEP_VIEWED, {
      operation: MIXPANEL_OPERATIONS.ONBOARDING_WIZARD_STEP_VIEWED,
      interaction_details: {
        ...baseWizardDetails,
        step_key: toStepKey(stepId),
        step_name: step ? toStepName(step) : stepId,
        step_index: stepIndex + 1,
        is_revisit: isRevisit,
      },
    });
  };

  const onStepNavigated = (
    stepIndex: number,
    stepId: string,
    action: WizardStepAction,
  ) => {
    const step = steps[stepIndex];

    mixpanelService.track(
      MIXPANEL_OPERATIONS.ONBOARDING_WIZARD_STEP_ACTION_CLICKED,
      {
        operation: MIXPANEL_OPERATIONS.ONBOARDING_WIZARD_STEP_ACTION_CLICKED,
        interaction_details: {
          ...baseWizardDetails,
          step_key: toStepKey(stepId),
          step_name: step ? toStepName(step) : stepId,
          step_index: stepIndex + 1,
          is_revisit: revisitedSteps.current.has(stepId),
          action,
        },
      },
    );
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    onStepNavigated(stepIndex, stepId, 'close');
  };

  const onWizardCompleted = () => {
    mixpanelService.track(MIXPANEL_OPERATIONS.ONBOARDING_WIZARD_COMPLETED, {
      operation: MIXPANEL_OPERATIONS.ONBOARDING_WIZARD_COMPLETED,
      interaction_details: {
        ...baseWizardDetails,
      },
    });
  };

  return { onStepViewed, onStepNavigated, onCancel, onWizardCompleted };
};
